import { useState } from "react";
import { X, DollarSign, Package, User as UserIcon, Rocket, Smartphone, Headphones, Signal, Shield } from "lucide-react";
import { api, formatApiError, formatBRL } from "../lib/api";
import { fireConfetti, fireBigConfetti } from "../lib/confetti";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "aparelho", label: "Aparelho", icon: Smartphone, color: "from-violet-500 to-fuchsia-500" },
  { key: "acessorio", label: "Acessório", icon: Headphones, color: "from-cyan-500 to-blue-500" },
  { key: "plano", label: "Plano", icon: Signal, color: "from-emerald-500 to-teal-500" },
  { key: "seguro", label: "Seguro", icon: Shield, color: "from-amber-500 to-orange-500" },
];

export default function NewSaleSheet({ open, onClose, onCreated }) {
  const [product, setProduct] = useState("");
  const [value, setValue] = useState("");
  const [client, setClient] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [xpPop, setXpPop] = useState(null);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const v = parseFloat(value.replace(",", "."));
    if (!product || !client || !v || v <= 0) {
      toast.error("Preencha todos os campos corretamente");
      return;
    }
    if (!category) {
      toast.error("Escolha o tipo da venda");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/sales", {
        product,
        value: v,
        client_name: client,
        category,
      });
      setXpPop(data.xp_earned);
      fireConfetti();
      if (v >= 1000) fireBigConfetti();
      toast.success(`Venda registrada! +${data.xp_earned} XP`);
      setTimeout(() => {
        setXpPop(null);
        setProduct(""); setValue(""); setClient(""); setCategory("");
        onCreated?.();
        onClose();
      }, 1200);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao registrar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="phone-frame w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="neon-card-violet mx-3 mb-3 p-6 slide-up relative overflow-hidden" data-testid="new-sale-sheet">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="chip chip-pink mb-2">⚡ Nova venda</p>
              <h2 className="font-arcade text-2xl font-bold text-white">Registrar Venda</h2>
            </div>
            <button
              onClick={onClose}
              data-testid="close-new-sale"
              className="w-10 h-10 rounded-full bg-slate-800/80 flex items-center justify-center text-slate-300 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>

          {xpPop && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <div className="xp-pop font-arcade text-5xl font-extrabold text-amber-400 drop-shadow-[0_0_20px_rgba(245,158,11,0.8)]">
                +{xpPop} XP
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-mono text-slate-400 uppercase tracking-widest mb-2 block">Tipo da venda</label>
              <div className="grid grid-cols-4 gap-2" data-testid="sale-category-group">
                {CATEGORIES.map((c) => {
                  const Ico = c.icon;
                  const active = category === c.key;
                  return (
                    <button
                      type="button"
                      key={c.key}
                      data-testid={`sale-category-${c.key}`}
                      onClick={() => setCategory(c.key)}
                      className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border transition-all ${
                        active
                          ? `bg-gradient-to-br ${c.color} border-white/30 shadow-lg scale-105`
                          : "bg-slate-900/60 border-slate-700 text-slate-400 hover:border-slate-500"
                      }`}
                    >
                      <Ico size={18} className={active ? "text-white" : "text-slate-400"} strokeWidth={2.2} />
                      <span className={`text-[10px] font-arcade font-semibold ${active ? "text-white" : "text-slate-400"}`}>
                        {c.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs font-mono text-slate-400 uppercase tracking-widest">Produto</label>
              <div className="relative mt-1">
                <Package className="absolute left-4 top-1/2 -translate-y-1/2 text-fuchsia-400" size={18} />
                <input
                  data-testid="sale-product-input"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  placeholder="Ex: Plano Premium"
                  className="arcade-input pl-11"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-mono text-slate-400 uppercase tracking-widest">Valor (R$)</label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-400" size={18} />
                <input
                  data-testid="sale-value-input"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="1500"
                  inputMode="decimal"
                  className="arcade-input pl-11 font-mono"
                />
              </div>
              {value && !isNaN(parseFloat(value.replace(",", "."))) && (
                <p className="text-xs text-cyan-300 mt-2 font-mono">
                  {formatBRL(parseFloat(value.replace(",", ".")))} • +
                  {Math.max(10, Math.floor(parseFloat(value.replace(",", ".")) / 10))} XP
                </p>
              )}
            </div>

            <div>
              <label className="text-xs font-mono text-slate-400 uppercase tracking-widest">Cliente</label>
              <div className="relative mt-1">
                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-400" size={18} />
                <input
                  data-testid="sale-client-input"
                  value={client}
                  onChange={(e) => setClient(e.target.value)}
                  placeholder="Nome do cliente"
                  className="arcade-input pl-11"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="sale-submit-btn"
              className="btn-arcade w-full flex items-center justify-center gap-2 text-lg disabled:opacity-50"
            >
              <Rocket size={20} />
              {loading ? "Registrando..." : "Registrar Venda"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
